// DeletePendingConfirm — admin-only soft-delete of a pending booking.
//
// Server contract: DELETE /pending/:enc_id (requireAdmin). Soft-delete sets
// `deleted_at = NOW()` and removes the row from the listing. Notes are
// retained on the database side.

import axios from 'axios';
import { Modal } from '../../../components/ui/Modal/Modal';
import { Button } from '../../../components/ui/Button/Button';
import { useToast } from '../../../hooks/useToast';
import { useDeletePending } from '../hooks/usePending';
import styles from './DeletePendingConfirm.module.css';

export interface DeletePendingConfirmProps {
  open: boolean;
  encId: string | null;
  reference?: string | null;
  onClose: () => void;
  onDeleted?: () => void;
}

export function DeletePendingConfirm({
  open,
  encId,
  reference,
  onClose,
  onDeleted,
}: DeletePendingConfirmProps): JSX.Element | null {
  const toast = useToast();
  const del = useDeletePending(encId ?? undefined);

  if (!open) return null;

  async function handleConfirm() {
    if (!encId) return;
    try {
      await del.mutateAsync();
      toast.success('Pending booking deleted.');
      onDeleted?.();
      onClose();
    } catch (err) {
      const msg = axios.isAxiosError(err)
        ? (err.response?.data as { message?: string } | undefined)?.message ??
          'Could not delete pending booking.'
        : 'Could not delete pending booking.';
      toast.error(msg);
    }
  }

  return (
    <Modal
      open={open}
      onClose={del.isPending ? () => undefined : onClose}
      title="Delete Pending Booking"
      size="sm"
      preventClose={del.isPending}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={del.isPending}>
            Go back
          </Button>
          <Button
            variant="danger"
            onClick={handleConfirm}
            loading={del.isPending}
          >
            Delete Permanently
          </Button>
        </>
      }
    >
      {reference ? (
        <p className={styles.subtitle}>Pending {reference}</p>
      ) : null}
      <div className={styles.dangerBox} role="alert">
        <strong>Warning: This action cannot be undone!</strong>
        <p>
          Deleting this booking will remove it from the pending list. All
          booking information and associated notes will no longer be visible.
        </p>
      </div>
    </Modal>
  );
}
